import type { SetupState } from '@tandemone/types'

import {
  BILLING_NAV_HREF,
  CERTIFICATE_REVIEW_NAV_HREF,
  DUE_DILIGENCE_NAV_HREF,
  RFI_NAV_HREF
} from './nav-groups'

/** the settings page that owns the capability picker. the banner and the badge
 *  both point here, so they can never send someone to different places. */
export const CAPABILITIES_SETTINGS_HREF = '/settings/supplier/capabilities'

/** counts sitting on nav rows, keyed by href.
 *
 *  these are open tasks, not notifications: nothing here is dismissable, and
 *  each clears itself only when the work behind it is done. that is the whole
 *  point next to the banners, which can be dismissed for good.
 */
export function getNavBadges(
  setup: SetupState | null,
  billingEnabled: boolean
): Record<string, number> {
  const badges: Record<string, number> = {}
  if (!setup) return badges

  if (setup.supplier?.needsCapabilitiesUpdate) {
    badges[CAPABILITIES_SETTINGS_HREF] = 1
  }
  /* the depth of each staff queue, not what this super has yet to look at:
   * nothing records who has seen which row, so a per-viewer count would be
   * inventing state. it clears when the queue is worked, which is the same
   * contract every other badge here holds. */
  if (setup.reviewQueue) {
    badges[DUE_DILIGENCE_NAV_HREF] = setup.reviewQueue.dueDiligence
    badges[CERTIFICATE_REVIEW_NAV_HREF] = setup.reviewQueue.certificates
  }
  /* same contract: an open task, not an unread marker. a locked recipient still
   * gets it, since the RFI is the reason to upgrade. */
  if (setup.pendingReceivedRfis > 0) {
    badges[RFI_NAV_HREF] = setup.pendingReceivedRfis
  }
  const ownBilling = billingEnabled && !setup.entitlements.billedThrough
  /* the entitlement, never a plan id: it clears the moment they are on a paid
   * plan, and stays put through a free one. hasEverSubscribed retires it for
   * good once they have bought at all, since a lapsed subscriber does not need
   * pointing at a page they already know. */
  if (
    ownBilling &&
    setup.isSupplier &&
    !setup.entitlements.canViewRfiDetail &&
    (!setup.entitlements.hasEverSubscribed ||
      setup.entitlements.activationFailure !== null)
  ) {
    badges[BILLING_NAV_HREF] = 1
  }
  /* an unpaid invoice outranks the upgrade nudge rather than adding to it: both
   * point at the same page, and two is not twice the task. */
  if (ownBilling && setup.entitlements.hasOpenInvoice) {
    badges[BILLING_NAV_HREF] = 1
  }
  return badges
}

/** a row's count includes its children's, so a collapsed group still shows
 *  there is something inside it.
 *
 *  summed over a set of hrefs rather than added up: a group whose href is also
 *  its first child's (Settings and Account & security are both /settings)
 *  would otherwise count the same badge twice.
 */
export function badgeFor(
  badges: Record<string, number>,
  item: { href: string; children?: { href: string }[] }
): number {
  const hrefs = new Set([
    item.href,
    ...(item.children?.map(child => child.href) ?? [])
  ])
  let total = 0
  for (const href of hrefs) total += badges[href] ?? 0
  return total
}
